import { useLayoutEffect, useRef } from 'react';

import { MediaMachineCategory } from './MediaMachine';
import { colors } from '@/resources/tailwindConfig';

const MediaMachineConnections = ({
    isEmbedded,
    categories,
    classifiedCategory,
    maxWidth,
    maxHeight,
}: {
    isEmbedded?: boolean;
    categories: MediaMachineCategory[];
    classifiedCategory: MediaMachineCategory | null;
    maxWidth?: number;
    maxHeight?: number;
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const convertRemToPixels = (rem: number) =>
        rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

    useLayoutEffect(() => {
        if (canvasRef.current && categories?.length) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (!context) return;

            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            context.clearRect(0, 0, canvas.width, canvas.height);

            const height = canvas.height - 100 / categories.length;
            const width = canvas.width;
            const baseVerticalSpace = height / categories.length;
            const baseVerticalOffset = baseVerticalSpace / 2;
            const baseHorizontalSpace = width / categories.length;
            const baseHorizontalOffset = baseHorizontalSpace / 2;

            const drawLine = (
                isClassifiedCategory: boolean,
                startCoordinates: number[],
                endCoordinates: number[],
                controlPointOffset: number[],
                lineWidth: number,
                color: string,
                samples: number,
            ) => {
                context.globalAlpha = isClassifiedCategory ? 1 : 0.5;
                context.strokeStyle = color;
                context.lineWidth =
                    Math.max(lineWidth * (1 - 3 / samples), lineWidth / 3) *
                    1.5;
                context.lineCap = 'round';
                context.beginPath();
                context.translate(0.5, 0.5);
                context.moveTo(startCoordinates[0], startCoordinates[1]);
                context.bezierCurveTo(
                    startCoordinates[0] + controlPointOffset[0],
                    startCoordinates[1] + controlPointOffset[1],
                    endCoordinates[0] - controlPointOffset[0],
                    endCoordinates[1] - controlPointOffset[1],
                    endCoordinates[0],
                    endCoordinates[1],
                );
                context.stroke();
            };

            const drawConnection = (
                category: MediaMachineCategory,
                index: number,
            ) => {
                if (index < 0) return;
                const isClassifiedCategory =
                    classifiedCategory?.id === category.id;
                const lineWidth = convertRemToPixels(
                    isClassifiedCategory ? 1 : 0.8,
                );
                const startCoordinates = isEmbedded
                    ? [width / 2, -lineWidth]
                    : [
                          -lineWidth,
                          baseVerticalOffset + index * baseVerticalSpace,
                      ];
                const endCoordinates = isEmbedded
                    ? [
                          baseHorizontalOffset + index * baseHorizontalSpace,
                          canvas.height + lineWidth,
                      ]
                    : [canvas.width + lineWidth, lineWidth + height / 2];
                const controlPointOffset = isEmbedded
                    ? [0, canvas.height]
                    : [canvas.width, 0];

                const [colorId, ...shadeParts] = category.color.split('-');

                drawLine(
                    isClassifiedCategory,
                    startCoordinates,
                    endCoordinates,
                    controlPointOffset,
                    lineWidth,
                    colors?.[colorId]?.[shadeParts.join('-') ?? '500'] ??
                        '#000',
                    category.samples,
                );
            };

            [...categories].reverse().forEach((c, i) => {
                const index = categories.length - i - 1;
                if (!categories[index]?.id || c.id == classifiedCategory?.id)
                    return;
                drawConnection(categories[index], index);
            });

            if (classifiedCategory)
                drawConnection(
                    classifiedCategory,
                    categories
                        .map((category) => category.id)
                        .indexOf(classifiedCategory.id),
                );
        }
    }, [
        categories,
        categories.length,
        classifiedCategory,
        canvasRef,
        maxWidth,
        maxHeight,
    ]);

    return (
        <canvas
            width="100%"
            className={`${
                isEmbedded
                    ? 'h-10 w-full max-w-sm'
                    : 'hidden md:block w-16 h-full shrink-0 self-center'
            }`}
            ref={canvasRef}
            style={{
                maxWidth: `${maxWidth}px`,
                maxHeight: isEmbedded ? '2rem' : `${maxHeight}px`,
                height: `${maxHeight}px`,
            }}
        />
    );
};

export default MediaMachineConnections;
